/* eslint-disable sonarjs/cognitive-complexity */
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import {AttrsSchema, Component} from 'panel';

import {AdditionalContext} from 'common/query-context';
import {
  CurrentProject,
  CurrentProjectResource,
  Project,
  ProjectsResource,
} from 'common/report/model-resources/projects';
import {CurrentUser, CurrentUserResource} from 'common/report/model-resources/users';
import {currentProjectStore} from 'common/report/stores/project-details';
import {supportInfoStore} from 'common/report/stores/support-info';
import {
  closestReportLink,
  debug,
  excludeDemos,
  getDownloadMode,
  getProjectId,
  getReportUrl,
  getWorkspaceId,
  isPersistableReport,
} from 'common/report/util';
import {spaNavManager} from 'common/report/util/spa-nav-manager';
import {WorkspaceObj, workspacesClient} from 'common/report/queries/app-api/workspaces-client';
import {
  getUserFlags,
  setUserFlag,
  unsetUserFlag,
  viewedReportFlagManager,
} from 'common/report/queries/app-api/user-flags';
import {getProductUpdates, markProductUpdatesAsSeen} from 'common/report/queries/app-api/product-updates-client';
import {events, properties, setPeopleProperties, track} from 'common/report/util/settings-tracking';
import {isProjectOnlyApp, makePathFromUrlState, parseStateFromUrl} from 'common/report/util/state-from-url';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {capitalize, nameToInitials, pluralize, truncateMiddle} from 'common/util/string';
import {chameleonReadyPromise, isChameleonAvailable, showTour, TourId} from 'common/util/chameleon';
import {getCookie, safeSetCookie} from 'common/util/cookie';
import {generateShortcutLabel, getCommandKey, keyEventSwitcher} from 'common/util/keyboard';
import {stickyElemHooks} from 'common/util/dom-sticky-stack';
import {FestiveMode, getFestiveMode, setFestiveMode} from 'common/util/festive-mode';
import {Model, ModelResource, ModelStatus} from 'common/util/model-resource/model-resource';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  ApiFailureDescription,
  sortWorkspaces,
  UISourceObject,
  workspaceApiFailure,
  workspaceCreateEvent,
  workspacesTrackingPrefix,
} from 'common/util/workspaces';
import {promptManageWorkspaceModal} from 'common/widgets/create-entity-modal';
import {LabelTheme, Section, SectionItem, SubLabelTheme} from 'common/components/items-menu/types';
import {
  DEFAULT_SEARCH_QUERY_STRING,
  getBrowseUrl,
  GetBrowseUrlOptions,
  getSearchContextFromReportName,
} from 'common/report/util/search';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {
  ContentDiscoveryType,
  getSearchCommonTrackingProperties,
  SearchPhaseVersion,
  SearchSource,
  SearchTrackedProperty,
} from 'common/report/util/search/tracking';
import {SwitchEl} from 'common/components/switch';
import {ToastType} from 'common/components/toast/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {reporter} from 'common/report/error-reporting';
import {openDemoProjectSelectorModal} from 'common/widgets/demo-selector-modal';
import {
  DownloadMode,
  LocalStorageKey,
  Permission,
  ProjectFeatureFlag,
  ProjectType,
  ReportName,
  UserFeatureFlag,
  UserFlag,
} from 'common/report/constants';
import {
  activatePersistedZendeskChat,
  activateZendeskChat,
  ChatDepartment,
  hideZendeskWidget,
} from 'common/report/external-widgets/zendesk';
import {UserIconEl} from 'common/widgets/user-icon';
import {layoutManager} from 'common/report/util/layout-manager';
import {IconSizeType} from 'common/constants';
import {IronContextRegistry} from 'common/report/context-registry';
import {purgeStorage} from 'common/report/purge-storage';
import {TextHighlightEl, THEME} from 'common/components/text-highlight';
import {SvgIconEl} from 'common/components/svg-icon';
import {usageAnalyticsClient} from 'common/report/queries/app-api/usage-analytics-client';
import {
  ChartDataEndpointPath,
  ChartDateRange,
  UsageAnalyticsContextTypes,
} from 'common/report/queries/app-api/usage-analytics-client-types';
import {fetchOrgProjectData} from 'common/report/queries/app-api/legacy/orgs-settings-client';
import {SearchDisplayContext} from 'common/report/util/search/types';
import {shouldOpenInNewTab} from 'common/util/dom';
import {promptFeedbackModal} from 'common/widgets/feedback-modal';
import {FeedbackForFeature} from 'common/widgets/feedback-modal/types';
import {captureWorkflowTiming, QueryStartEvent} from 'common/util/workflow-timing';
import {userDetailsStore} from 'common/report/stores/user-details';
import {userFlagsStore} from 'common/report/stores/user-flags';
import {featureSamplingUtils} from 'common/report/util/feature-sampling';
import {CreditBalanceInfo, CreditInfo} from 'common/report/queries/app-api/project-metadata-client';
import {numberInUSD} from 'common/util';
import {promptFYTModal} from 'common/widgets/find-your-team-modal';
import {promptIntroModal} from 'common/widgets/intro';

import {NavigationTrackingEvent} from './reports-menu/tracking';
import {
  AppMenuApp,
  ChromeHeaderState,
  HelpOnboardingSectionItemValue,
  HelpProductUpdateSectionItemValue,
  LastUserProjectState,
  NavCategories,
  NavReports,
  OrgInfo,
  PersistedDarkModeState,
  ReportContext,
  ReportPersistenceCookie,
} from './types';
import {
  ALL_PRODUCT_UPDATES_LINK,
  CHAMELEON_TOUR_QUERY_PARAM,
  DARK_MODE_KEY,
  HELP_GETTING_STARTED_SECTIONS,
  HelpLabels,
  HelpSectionItemType,
  HelpTrackingEvent,
  LAST_USER_PROJECT_KEY,
  NavCategoryType,
  REPORT_INFO,
} from './constants';
import {getNavCategories, getNavReportsFromCategory, getProductUpdateSections} from './util';
import {template} from './template';
import css from './index.inline.styl';
import './reports-menu';
import './festive-mode-header-anim';

const REPORT_PERSISTENCE_COOKIE = `mp_persistence`;

const TOAST_TTL = 7000;
const toastCtaCallbackMap = new Map<string, () => void>();

const SETTINGS_CATEGORY_TRACKING = {
  Org: events.viewOrg,
  Account: events.viewAccountSettings,
  Project: events.viewProjectSettings,
  Workspace: events.viewWorkspaceSettings,
  GettingStarted: events.setupMixpanel,
  BillingDetails: events.billingDetails,
  StartupCreditPromoClicked: events.clickStartupPromoLink,
};

interface NavigateOptions {
  fromAnchorlink?: boolean;
  keepReportContext?: boolean;
  additionalContext?: AdditionalContext;
}
export interface ChromeHeaderAttrs {
  /** used by dashboards to hide chrome-header in tv mode */
  hidden: boolean;
}

function isHelpOnboardingItem(item: SectionItem<any>): item is SectionItem<HelpOnboardingSectionItemValue> {
  return item.value?.type === HelpSectionItemType.GettingStarted;
}

function isHelpProductUpdateItem(item: SectionItem<any>): item is SectionItem<HelpProductUpdateSectionItemValue> {
  return item.value?.type === HelpSectionItemType.ProductUpdate;
}

function isSavedCoreReportUrl(url: string) {
  if (!/^https?:/.test(url)) {
    url = `${window.location.protocol}//${window.location.hostname}${url}`;
  }

  const {pathname, hash} = new URL(url);
  const state = parseStateFromUrl(pathname);

  if (![`insights`, `funnels`, `flows`, `retention`].includes(state.appName)) {
    return false;
  }

  return /^#(view|report)\/\d+/.test(hash);
}

export class ChromeHeader extends Component<ChromeHeaderState, ChromeHeaderAttrs, any, any, IronContextRegistry> {
  private lastUserProjectStorage: UserLocalStorageHelper<LastUserProjectState>;
  private userLocalStorageDarkModeHelper: UserLocalStorageHelper<PersistedDarkModeState>;
  private startupCreditPromoFeatureEligible: boolean | null = null;

  static get attrsSchema(): AttrsSchema<ChromeHeaderAttrs> {
    return {
      hidden: `boolean`,
    };
  }

  get config() {
    const defaultState: ChromeHeaderState = {
      categories: null,
      createProjectError: null,
      createProjectName: null,
      currentCategoryName: ``,
      currentUrlPath: ``,
      currentReportName: ``,
      darkMode: false,
      errorAlertMsg: null,
      inviteModalOrgs: [],
      isAtProjectMax: false,
      isNewUser: false,
      isOpen: {
        applicationsMenu: false,
        createProjectModal: false,
        helpMenu: false,
        inviteModal: false,
        searchModal: false,
        projectMenu: false,
        searchMenu: false,
        setupMenu: false,
        userMenu: false,
        starterBoardModal: false,
      },
      isSupportWidgetMaximized: false,
      projectId: null,
      projectMenuWorkspaces: [],
      productUpdates: [],
      searchedText: DEFAULT_SEARCH_QUERY_STRING,
      selectedCategory: ``,
      showNewProjectInput: false,
      showSupportWidget: false,
      viewedReportCount: 0,
      viewedStartupCreditPromo: false,
      dismissedOnboardingChecklist: false,
      enableOnboardingChecklist: false,
      showOnboardingChecklistHelpTooltip: false,
    };

    return {
      css,
      template,
      useShadowDom: true,
      defaultState,
      contexts: [
        `localStorageManager` as const,
        `urlManager` as const,
        `currentProject` as const,
        `currentUser` as const,
        `projects` as const,
      ],
      helpers: {
        getFestiveMode,
        stickyElemHooks,

        currentProjectStoreHasValue: () => !!this.getCurrentProject(),
        getLastUserProjectVisitedId: (): number | undefined => {
          return (
            (this.lastUserProjectStorage &&
              this.lastUserProjectStorage.getItem({
                key: LAST_USER_PROJECT_KEY,
                userId: userCookieUtils.getUserIdentityFromCookie()?.id,
              })?.projectId) ||
            undefined
          );
        },
        hasProjectFeatureFlag: (flag: ProjectFeatureFlag) => this.getCurrentProject()?.hasFeatureFlag(flag) ?? false,
        isDemoProject: () => this.getCurrentProject()?.isDemo ?? true, // Assume restrictive access
        shouldDisplayNavigationOptions: () => {
          const {projectId, currentReportName, categories} = this.state;
          const workspaceId = this.currentWorkspaceId;
          // this happens when user has first signed up and they don't have any projects to navigate to
          if (!projectId) {
            return false;
          }

          // don't show navigation options if we don't have categories to show
          if (!categories) {
            return false;
          }

          // hides navigation options when:
          // 1. a user is in Data View library but we can't infer a workspaceId
          // 2. a user does not belong to any data views
          // when a project contains workspaces, we should always navigate to workspace based reports
          return !(currentReportName === `views` && workspaceId === null);
        },

        allMenusClosed: () => !Object.values(this.state.isOpen).find((menu) => menu),

        updateOpen: (updated?: ChromeHeaderState['isOpen']) => {
          if (updated) {
            this.update({isOpen: {...this.state.isOpen, ...updated}});
          } else {
            // if not provided a specific update, just close everything
            this.update({
              isOpen: Object.keys(this.state.isOpen).reduce((o, key) => Object.assign(o, {[key]: false}), {}),
            });
          }
        },

        getProjectAndWorkspaceName: (truncated: boolean) => {
          const projectName = this.getCurrentProjectName();
          const workspaceName =
            this.helpers.areWorkspacesActivated(this.getCurrentProject()?.id) &&
            (this.helpers.getCurrentWorkspace()?.name || ``);
          const separator = ` · `;

          if (truncated) {
            // maxCharLen constant accommodates the project selector such that chrome-header doesn't overflow 1150px.
            // 1150px is the minimum viewport width we support defined in global.styl
            const maxCharLen = 46;
            if (workspaceName) {
              const truncatedWorkspaceName = truncateMiddle(workspaceName, maxCharLen / 2);
              const remainingLen = maxCharLen - truncatedWorkspaceName.length - separator.length;
              const truncatedProjectName = truncateMiddle(projectName, remainingLen);
              return truncatedProjectName + separator + truncatedWorkspaceName;
            } else {
              return truncateMiddle(projectName, maxCharLen);
            }
          } else {
            return workspaceName ? projectName + separator + workspaceName : projectName;
          }
        },

        getProjectItems: (projects: Project[]): SectionItem[] =>
          projects
            .map((proj) => {
              const workspaceCount = this.getWorkspacesForProject(proj.id).length;
              const workspaceOverrides = this.helpers.areWorkspacesActivated(proj.id)
                ? {
                    subLabel: `${workspaceCount} ${pluralize(`Data View`, workspaceCount)}`,
                    hasCaret: workspaceCount > 0,
                  }
                : {};

              return {
                label: proj.name || `My New Project`,
                subLabel: proj.isRollupProject() && this.helpers.getOrgs().length > 1 ? `Rollup Project` : ``,
                projectId: proj.id,
                type: proj.type,
                ...workspaceOverrides,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label)),

        selectProjectMenuBackClick: () => this.update({projectMenuWorkspaces: []}),

        getProjectsForMenu: (): Project[] => excludeDemos(this.getAllProjects()),
        getProjectsMenuSections: (): Section[] => {
          let orgList;
          const allProjects = this.helpers.getProjectsForMenu();
          const projectMenuWorkspaces = this.state.projectMenuWorkspaces;
          let sections: Section[];

          if (this.helpers.areWorkspacesActivated()) {
            sections = [
              {
                items: projectMenuWorkspaces.map((workspace) => ({
                  label: workspace.name,
                  id: workspace.id,
                  isWorkspace: true,
                  projectId: workspace.projectId,
                  tooltip: {
                    text: `${workspace.name}\nCreated by ${workspace.creator}\n\n${workspace.description}\n[More details](/settings/project/${workspace.projectId}/views/detail/${workspace.id})`,
                    size: DisplaySize.Medium,
                  },
                })),
              },
              {
                items: [
                  {
                    label: `Data View Library`,
                    icon: `open`,
                    projectId: projectMenuWorkspaces[0].projectId,
                    href: getReportUrl(`views`, {projectId: projectMenuWorkspaces[0].projectId}),
                  },
                ],
              },
            ];
          } else if ((orgList = this.helpers.getOrgs({excludeDemoOrgs: true})).length > 1) {
            sections = orgList.map((org) => ({
              label: orgList.length > 1 ? org.name : null,
              items: this.helpers.getProjectItems(allProjects.filter((p: Project) => p.organizationId === org.id)),
            }));
          } else {
            const projects = allProjects.filter((p) => !p.isRollupProject());
            const rollupProjects = allProjects.filter((p) => p.isRollupProject());
            const hasRollupProjects = rollupProjects.length > 0;
            sections = [
              {
                // Only explicitly label if there are rollup projects, otherwise its just a single list
                label: hasRollupProjects ? `Projects` : ``,
                items: this.helpers.getProjectItems(projects),
              },
            ];

            if (hasRollupProjects) {
              sections.push({
                label: `Rollup Projects`,
                items: this.helpers.getProjectItems(rollupProjects),
              });
            }
          }
          if (!this.helpers.areWorkspacesActivated()) {
            sections.push({
              label: `Mixpanel Demo`,
              items: [
                {
                  label: `Sample Datasets`,
                  icon: `open`,
                  projectId: 0,
                  onClick: () => {
                    openDemoProjectSelectorModal((ev) => {
                      this.addToast(ev.detail.message, ToastType.ERROR);
                    });
                  },
                  trackEvent: `[Demo Project] Click Explore Demo`,
                  trackProps: {'Navigated From': `Project Menu`},
                },
              ],
            });
          }

          // cap sections to max of 10 items so list isn't insanely long
          for (const section of sections) {
            section.showLimit = section.items.length > 10 ? 5 : 10;
          }

          return sections;
        },

        getSearchModalDisplayContext: (): SearchDisplayContext => {
          const {currentReportName} = this.state;
          if (
            [
              ReportName.Dashboards,
              ReportName.Flows,
              ReportName.Funnels,
              ReportName.Insights,
              ReportName.Retention,
              ReportName.Impact,
            ].includes(currentReportName as ReportName)
          ) {
            return getSearchContextFromReportName(currentReportName as ReportName);
          }
          return null;
        },

        handleOpenReportsAndDashboardClicked: () => {
          this.helpers.updateOpen({searchModal: true});
          mixpanel.track(`[Header] Search Report Clicked`);
        },

        getHelpMenuSections: (): Section[] => {
          const {supportChatEnabled} = supportInfoStore.value;
          const productUpdateSections = getProductUpdateSections(this.state.productUpdates);

          return [
            {
              items: [
                {
                  icon: `help-center`,
                  label: HelpLabels.Docs,
                  href: `https://developer.mixpanel.com/docs`,
                  target: `_blank`,
                },
                {
                  label: HelpLabels.GettingStarted,
                  subSections: [this.getGettingStartedSubMenu()],
                },
                {
                  label: HelpLabels.ProductUpdates,
                  subSections: productUpdateSections,
                  href: productUpdateSections ? null : ALL_PRODUCT_UPDATES_LINK,
                  target: `_blank`,
                },
                {
                  label: HelpLabels.DemoProject,
                },
                {
                  label: HelpLabels.YourPrivacyRights,
                  href: `/cpra-preference-center`,
                  target: `_blank`,
                },
                {
                  label: HelpLabels.Community,
                  href: `https://experiences.mixpanel.com/mixpanel-slack-community/`,
                  target: `_blank`,
                },
                {
                  label: HelpLabels.HireAnExpert,
                  href: `https://mixpanel.com/partners/experts`,
                  target: `_blank`,
                },
              ],
              iconRenderer: (item) => {
                if (item.label === HelpLabels.GettingStarted && this.state.isNewUser) {
                  const subSectionLength = HELP_GETTING_STARTED_SECTIONS.items.length.toString();
                  return TextHighlightEl({
                    attrs: {theme: `negative`, text: subSectionLength.toString(), length: `wide`},
                  });
                } else if (item.label === HelpLabels.ProductUpdates) {
                  const unseenProductUpdates = this.getUnseenProductUpdates();
                  if (unseenProductUpdates.length) {
                    return TextHighlightEl({
                      attrs: {theme: `negative`, text: unseenProductUpdates.length.toString(), length: `wide`},
                    });
                  }
                }

                return SvgIconEl({attrs: {icon: item.icon}});
              },
            },
            {
              items: (this.getCurrentProject()?.externalHelpLinks || []).map((link) => ({
                label: link.linkName,
                href: link.linkURL,
                target: `_blank`,
                isDisabled: !link.linkName,
                icon: this.getHelpLinkIcon(link.linkURL),
              })),
            },
            {
              items: [
                {
                  label: HelpLabels.SubmitFeedback,
                },
                supportChatEnabled && {
                  label: HelpLabels.LiveChat,
                  icon: `sms`,
                },
                {
                  label: HelpLabels.ContactUs,
                  icon: `email`,
                  subSections: [
                    {
                      items: [
                        !this.isGuestUser && {
                          label: HelpLabels.ContactSupport,
                        },
                        {
                          label: HelpLabels.ContactSales,
                        },
                      ].filter(Boolean),
                    },
                  ],
                },
              ].filter(Boolean),
            },
          ];
        },

        /** areWorkspacesActivated a boolean to indicate the project has actually started to utilize workspaces[dataviews] */
        areWorkspacesActivated: (projectId?: number) => {
          const projectMenuWorkspaces = !projectId
            ? this.state.projectMenuWorkspaces
            : this.getWorkspacesForProject(projectId);

          return (
            projectMenuWorkspaces.length > 1 ||
            (projectMenuWorkspaces.length === 1 && this.getProject(projectMenuWorkspaces[0].projectId)?.hasWorkspaces)
          );
        },
        getCurrentWorkspace: () => this.getCurrentProject()?.workspaces[this.currentWorkspaceId],

        getUrlForProject: (projectId: number) => {
          const workspaceId = get(this.getWorkspacesForProject(projectId)[0], `id`);
          return makePathFromUrlState({projectId: `${projectId}`, workspaceId: `${workspaceId}`});
        },

        updateCurrentCategoryAndReportNameFromUrl: () => {
          const location = this.getUrlManager().getLocation();
          const reportInfo = this.getReportInfoFromUrl(new URL(location.href));
          if (reportInfo) {
            this.update({
              currentCategoryName: reportInfo.categoryName,
              currentReportName: reportInfo.reportName,
            });
            this.persistLastSeenUrlData();
          }
        },

        hasOpenModal: (): boolean => {
          return Object.keys(this.state.isOpen).some((k) => this.state.isOpen[k]);
        },

        initializeSpaNavManager: async () => {
          const currentProject = this.getCurrentProject();
          if (!currentProject) {
            return;
          }
          // multiple async entry points, use the one that's ready
          await spaNavManager.initialize({
            projectId: String(this.state.projectId),
            projectName: currentProject.name,
          });
        },

        // TODO
        // eslint-disable-next-line sonarjs/cognitive-complexity
        navigate: async (
          url: string,
          ev = null,
          {fromAnchorlink = false, keepReportContext = false, additionalContext = {}}: NavigateOptions = {},
        ) => {
          const {currentUrlPath} = this.state;

          // If we are keeping context between reports we must navigate via spa nav.
          if (keepReportContext) {
            await this.helpers.initializeSpaNavManager();
          }

          if (shouldOpenInNewTab(ev)) {
            // if anchor link, browser will do the right thing by opening in new tab anyway
            if (!fromAnchorlink) {
              window.open(url, `_blank`);
            }
          } else {
            if (spaNavManager.isNavigable(url, currentUrlPath)) {
              // this will be a fake page navigation by swapping the iron app js bundle and css
              // we'll ask the window's beforeunload whether we should ask the user to stay on the page
              let shouldNavigate = true;
              if (window.onbeforeunload instanceof Function) {
                const beforeUnloadEv = new CustomEvent(`beforeunload`, {cancelable: true});
                window.onbeforeunload(beforeUnloadEv);
                if (beforeUnloadEv.defaultPrevented) {
                  shouldNavigate = confirm(`Changes you made may not be saved. Leave this page?`);
                }
              }

              if (shouldNavigate) {
                spaNavManager.navigate(url, currentUrlPath, ev, keepReportContext, additionalContext);
                this.helpers.updateCurrentCategoryAndReportNameFromUrl();
                this.update({currentUrlPath: url});
              } else {
                // user said no, don't navigate
                if (ev) {
                  if (ev.type === `popstate`) {
                    // popstate event immediately overwrites the url even though the user didn't want to leave
                    // push back the old url
                    window.history.pushState(null, null, currentUrlPath);
                  } else {
                    ev.preventDefault();
                  }
                }
              }
            } else {
              this.update({currentUrlPath: url});

              // if an anchor link is clicked, let the browser do it's thing
              // otherwise onbeforeunload will fire twice if we also set location.href
              // see https://mixpanel.atlassian.net/browse/RNA-737
              if (!fromAnchorlink) {
                window.location.href = url;
              }

              const handleBeforeUnload = (beforeUnloadEv: BeforeUnloadEvent) => {
                // ev.defaultPrevented == true means the user was prompted to stay or leave.
                // we have no way to know what their choice was
                // so don't do an optimistic report whiteout and select the new report tab
                // if user leaves, the right state will be shown anyway after the page loads, just a bit slower
                if (!beforeUnloadEv.defaultPrevented) {
                  // optimistic update that the new page started loading instantly
                  // by showing whiteout loader spinny and selecting the new report in nav
                  this.helpers.updateCurrentCategoryAndReportNameFromUrl();
                  document.querySelector(`.whiteout_loader`)?.classList.remove(`hidden`);
                }
              };

              window.addEventListener(`beforeunload`, handleBeforeUnload, {once: true});
            }
          }
        },

        handleHistoryStateChange: (ev: Event) => {
          this.helpers.updateCurrentCategoryAndReportNameFromUrl();

          // NOTE: popstate is fired for history forward/back and location hash changes
          // we only navigate if the pathname changes, for hash only changes, let the panel router handle changes
          // otherwise we'll get a 'Maximum call stack size exceeded' error as navigate() causes another popstate
          if (ev.type === `popstate`) {
            const prevPathname = this.state.currentUrlPath.split(`#`)[0];
            const location = this.getUrlManager().getLocation();
            const curPathname = location.pathname;
            if (curPathname !== prevPathname) {
              this.helpers.navigate(curPathname + location.hash, ev);
            }
          }
        },

        selectProjectMenuItem: (ev) => {
          const {item} = ev.detail;
          if (item.projectId) {
            this.helpers.navigateToProject(ev);
          }
          if (item.href || item.onClick) {
            // track, call onClick, and navigate in that order,
            // so that all three can be done if need be
            if (item.trackEvent) {
              mixpanel.track(item.trackEvent, item.trackProps || {});
            }
            if (item.onClick) {
              item.onClick();
            }
            if (item.href) {
              this.helpers.navigate(item.href, ev, {fromAnchorlink: true});
            }
          }
        },

        navigateToBoard: (dashboardId: number, projectId: number, workspaceId?: number): Promise<void> => {
          const wsId = workspaceId || this.getWorkspacesForProject(projectId)[0]?.id || undefined;
          const targetUrl = getReportUrl(`boards/#id=${dashboardId}`, {
            projectId,
            workspaceId: wsId,
          });
          return this.helpers.navigate(targetUrl);
        },

        // eslint-disable-next-line sonarjs/cognitive-complexity
        navigateToProject: (ev) => {
          const {item} = ev.detail;
          const projectId = item.projectId;
          const projectDomain = this.getCurrentProject()?.domain;
          const workspaceId = item.isWorkspace ? item.id : get(this.getWorkspacesForProject(projectId)[0], `id`);
          const location = this.getUrlManager().getLocation();

          if (item.hasCaret) {
            const projectMenuWorkspaces = this.getWorkspacesForProject(projectId);
            if (projectMenuWorkspaces.length) {
              this.update({projectMenuWorkspaces});
              return;
            }
          }

          this.helpers.updateOpen({projectMenu: false});
          this.update({projectMenuWorkspaces: []});

          // e.g if user is in insights, navigate to insights in new project or workspace
          let navigatePath = makePathFromUrlState({projectId, workspaceId});

          // if switching between workspaces for the same project, preserve the hash for reports to keep selected user report
          if (
            navigatePath &&
            `${workspaceId}` !== `${this.currentWorkspaceId}` &&
            `${projectId}` === `${this.state.projectId}`
          ) {
            navigatePath += location.hash;
          }

          if (!isPersistableReport() || !navigatePath) {
            // let django navigate to last persisted report
            // trailing slashes are important otherwise we get an extra 301 roundtrip
            navigatePath = workspaceId ? `/report/${projectId}/view/${workspaceId}/` : `/report/${projectId}/`;
          }

          if (workspaceId) {
            const allProjectWorkspaces = this.getWorkspacesForProject(projectId);
            const hasGlobalWorkspaceAccess = allProjectWorkspaces.some((w) => w.isGlobal);
            const hasDefaultWorkspaceAccess = allProjectWorkspaces.some((w) => w.isDefault);
            const selectedWorkspace: WorkspaceObj = allProjectWorkspaces.find(
              (w: WorkspaceObj) => w.id === workspaceId,
            );
            mixpanel.track(`Selected Workspace`, {
              'Selected Project ID': projectId,
              'Selected Workspace ID': workspaceId,
              'Selected Workspace Name': ev.detail.item.label,
              'Selected Workspace Is Global': selectedWorkspace.isGlobal,
              'Selected Workspace Is Default': selectedWorkspace.isDefault,
              'User Has Global Workspace Access': hasGlobalWorkspaceAccess,
              'User Has Default Workspace Access': hasDefaultWorkspaceAccess,
              'Total Available Workspaces': allProjectWorkspaces.length,
              // track url pattern e.g /report/<pid>/view/<wid>/insights. remove trailing slashes
              'Navigate Url Pattern': navigatePath
                .replace(projectId, `<pid>`)
                .replace(workspaceId, `<wid>`)
                .replace(/\/$/, ``),
            });
          } else {
            mixpanel.track(`Selected Project`, {
              'Selected Project ID': projectId,
              'Selected Project Name': ev.detail.item.label,
              'Selected Project Type': ev.detail.item.type === ProjectType.Rollup ? `Rollup Project` : `Project`,
              // track url pattern e.g /report/<pid>/insights. remove trailing slashes
              'Navigate Url Pattern': navigatePath.replace(projectId, `<pid>`).replace(/\/$/, ``),
            });
          }

          // This logic only applies when we are explicitly switching projects and not report types,
          // so we keep it out of helpers.navigate to avoid an adding another argument to a complex function.
          // we only want to switch domains for staging and prod i.e (x.)?mixpanel.com domains,
          // not for devboxes or localhost standalone mode
          const curDomain = window.location.hostname;
          const isMixpanelDotCom = (domain: string) => /\bmixpanel\.com$/.test(domain);
          if (isMixpanelDotCom(curDomain) && projectDomain && projectDomain !== curDomain) {
            this.getUrlManager().setLocation(`//` + projectDomain + navigatePath);
          } else {
            this.helpers.navigate(navigatePath, ev);
          }
        },

        clickedCreateProject: () => {
          this.helpers.updateOpen({createProjectModal: true, projectMenu: false});
        },

        updateCreateProjectName: (ev) => this.update({createProjectName: ev.target.value}),

        clickedCancel: () => this.helpers.updateOpen({createProjectModal: false}),

        projectNameValid: () => Boolean(this.state.createProjectName) && Boolean(this.state.createProjectName.trim()),

        clickedCreate: (ev) => {
          try {
            // comes from CreateProjectModalEl
            if (ev?.detail?.error) {
              throw new Error(ev.detail.error);
            }
            const newProjectId = ev?.detail?.response?.id;
            this.helpers.navigate(`/report/${newProjectId}/`);
          } catch (error) {
            this.update({createProjectError: error});
          }
        },

        getOrgsWithCreateProjectPermission: () => this.helpers.getOrgs({permissions: [Permission.CreateProject]}),

        orgHasPerm: (org, perm) => this.orgHasPerm(org, perm),

        orgIdForCreateProject: () => this.helpers.getOrgsWithCreateProjectPermission()[0]?.id,

        userInitials: () => nameToInitials(this.getCurrentUser()?.name),

        getOrgs: ({permissions = null, excludeDemoOrgs = false} = {}) => {
          let orgList = Object.values(this.getCurrentUser()?.organizations || {});
          if (permissions) {
            orgList = orgList.filter((o) => permissions.every((p) => o.permissions && o.permissions.includes(p)));
          }
          if (excludeDemoOrgs) {
            orgList = excludeDemos(orgList);
          }
          return orgList;
        },

        hasOrgs: () => !isEmpty(this.getCurrentUser()?.organizations),

        // warm up the slowest and most often used usage analytics charts when a user clicks on the cog.
        warmUsageAnalyticsData: () => {
          if (this.getCurrentProject()?.hasPermission(Permission.ViewUsageAnalytics)) {
            usageAnalyticsClient.getChartData({
              usageContext: UsageAnalyticsContextTypes.Project,
              id: this.state.projectId,
              chartEndpoint: ChartDataEndpointPath.WeeklyActiveUsers,
              chartDateRange: ChartDateRange.Weeks12,
            });

            usageAnalyticsClient.getChartData({
              usageContext: UsageAnalyticsContextTypes.Project,
              id: this.state.projectId,
              chartEndpoint: ChartDataEndpointPath.MostActiveUsers,
              chartDateRange: ChartDateRange.Weeks12,
            });
          }

          const organizationId = this.getCurrentProject()?.organizationId;
          if (
            this.getCurrentUser()?.organizations[organizationId]?.permissions.includes(Permission.ViewUsageAnalytics)
          ) {
            usageAnalyticsClient.getChartData({
              usageContext: UsageAnalyticsContextTypes.Organization,
              id: organizationId,
              chartEndpoint: ChartDataEndpointPath.AverageWeeklyActiveUsersByProject,
              chartDateRange: ChartDateRange.Weeks12,
            });

            usageAnalyticsClient.getChartData({
              usageContext: UsageAnalyticsContextTypes.Organization,
              id: organizationId,
              chartEndpoint: ChartDataEndpointPath.MostActiveUsersByProject,
              chartDateRange: ChartDateRange.Weeks12,
            });
          }

          if (this.getCurrentUser()?.organizations[organizationId]?.permissions.includes(Permission.ViewOrgProjects)) {
            fetchOrgProjectData(organizationId, {includeProjectUsageData: true});
          }
        },

        menuItemSelected: (ev) => {
          const {item}: {item: SectionItem} = ev.detail;
          const location = this.getUrlManager().getLocation();
          if (item.href) {
            this.trackSettingsMenuItemSelected(item);
            if (item.href === `/pricing/plan-builder`) {
              this.setViewedStartupCreditPromo();
              track(SETTINGS_CATEGORY_TRACKING.StartupCreditPromoClicked, {'Navigated From': `Settings Menu`});
            }
            this.helpers.navigate(item.href, ev, {fromAnchorlink: true});
          } else {
            switch (item.label) {
              case `Leave Demo`:
              case `Log Out`: {
                purgeStorage(this.getContext(`localStorageManager`));
                mixpanel.reset();
                // TODO @noj: ask around if we still need idmgmnt tracking, it's not initialized for volt
                mixpanel.idmgmt?.reset();
                location.href = `/logout`;
                break;
              }
              case `Invite Users`:
                this.helpers.openInviteUserModal();
            }
          }
        },

        openInviteUserModal: () => {
          const orgUrlMatch = /^\/settings\/org\/(\d+)/.exec(location.pathname);
          const orgId = parseInt(orgUrlMatch && orgUrlMatch[1], 10) || null;
          const orgs = this.helpers.getOrgs({permissions: [Permission.AddUser]});
          const org = orgs.find((o) => o.id === orgId);
          // if org id is specified by url and that org has `add_user` permission, show
          // invite modal for that org; otherwise show modal for all orgs with `add_user`
          // (user will choose org in modal)
          const inviteModalOrgs = org ? [org] : orgs;
          if (inviteModalOrgs.length) {
            this.helpers.updateOpen({inviteModal: true});
            this.update({inviteModalOrgs});
          }
        },

        closeSupportWidget: () => {
          this.update({showSupportWidget: false});
        },

        helpMenuItemSelected: (ev) => {
          const {item} = ev.detail;

          // Handle "Getting Started" and "Product Update" subsections.
          if (isHelpOnboardingItem(item)) {
            const {helpLink} = item.value;

            mixpanel.track(HelpTrackingEvent.GettingStarted, {
              'help link': helpLink,
              label: item.label,
            });

            window.open(helpLink, `_blank`);

            return;
          } else if (isHelpProductUpdateItem(item)) {
            const {tourId, helpLink, reportName} = item.value;
            const {projectId} = this.state;
            const workspaceId = this.currentWorkspaceId;
            const canShowTour = tourId && isChameleonAvailable();

            mixpanel.track(HelpTrackingEvent.WhatsNew, {
              'tour id': tourId,
              'help link': helpLink,
              'with chameleon': isChameleonAvailable(),
              label: item.label,
            });

            // Launch a tour if provided and possible, else open up the provided help doc.
            //TODO shayan or gonzalo temporary fix, show tour for dashboard chameleons from anywhere
            if (canShowTour && (reportName === this.state.currentReportName || reportName === ReportName.Dashboards)) {
              showTour(tourId);
            } else {
              let link = helpLink;
              if (canShowTour) {
                link = `${getReportUrl(reportName, {projectId, workspaceId})}?${CHAMELEON_TOUR_QUERY_PARAM}=${tourId}`;
              }

              window.open(link, `_blank`);
            }

            return;
          }

          switch (item.label) {
            case HelpLabels.DemoProject:
              openDemoProjectSelectorModal((ev) => {
                this.addToast(ev.detail.message, ToastType.ERROR);
              });
              mixpanel.track(`[Demo Project] Click Explore Demo`, {
                'Navigated From': `Project Menu`,
              });
              break;
            case HelpLabels.Docs:
              mixpanel.track(HelpTrackingEvent.Docs);
              break;
            case HelpLabels.AskQuestions:
            case HelpLabels.ContactSupport:
              this.openContactSupport();
              break;
            case HelpLabels.ContactSales:
              window.open(`https://mixpanel.com/contact-us/sales/`, `_blank`);
              mixpanel.track(HelpTrackingEvent.ContactSales);
              break;
            case HelpLabels.SubmitFeedback:
              promptFeedbackModal({
                feature: FeedbackForFeature.General,
                'is-staff-user': this.getCurrentUser()?.isStaff,
              });
              break;
            case HelpLabels.LiveChat:
              this.hideAllWidgets();
              activateZendeskChat(ChatDepartment.Support, this.getCurrentUser()?.name, this.getCurrentUser()?.email);
              break;
            case HelpLabels.Intro:
              mixpanel.track(HelpTrackingEvent.Intro);
              promptIntroModal({'first-name': this.getFirstName()});
              break;
            case HelpLabels.OnboardingGuide:
              mixpanel.track(HelpTrackingEvent.OnboardingGuide);
              this.update({dismissedOnboardingChecklist: false});
              unsetUserFlag(UserFlag.DismissedOnboardingChecklist);
              break;
            case HelpLabels.Community:
              mixpanel.track(HelpTrackingEvent.Community);
              break;
            case HelpLabels.HireAnExpert:
              mixpanel.track(HelpTrackingEvent.HireAnExpert);
              break;
            default:
              mixpanel.track(HelpTrackingEvent.ExternalLink, {
                'External Link Name': item.label,
                'External Link URL': item.href,
              });
              break;
          }
        },

        closeInviteModal: () => {
          this.helpers.updateOpen({inviteModal: false});
        },

        showOnboardingChecklist: () => {
          if (this.helpers.isDemoProject() || this.state.isOpen.starterBoardModal) {
            return false;
          }

          const queryParams = queryString.parse(window.location.search);
          if (queryParams[`from_fyt`]) {
            return false;
          }

          return !this.state.dismissedOnboardingChecklist && this.state.enableOnboardingChecklist;
        },

        selectMenuChanged: (menuType: string, isOpen: boolean) => {
          if (menuType === `project` && isOpen) {
            this.update({projectMenuWorkspaces: this.getWorkspacesForProject(this.state.projectId)});
          }

          if (menuType === `help` && !isOpen) {
            this.update({isNewUser: false});
            this.markProductUpdatesAsSeen();
          }

          this.helpers.updateOpen({[`${menuType}Menu`]: isOpen});
          if (isOpen) {
            mixpanel.track(NavigationTrackingEvent.OpenPrimaryMenu, {
              Menu: menuType,
              [SearchTrackedProperty.PhaseVersion]: this.getSearchPhaseVersion(),
              ...(menuType === `search` ? this.getSearchCommonProperties() : null),
            });
          }
        },

        selectCategory: (catName: string) => {
          if (catName) {
            mixpanel.track(NavigationTrackingEvent.OpenPrimaryMenu, {
              Menu: catName,
              [SearchTrackedProperty.PhaseVersion]: this.getSearchPhaseVersion(),
            });
          }
          this.helpers.updateOpen({
            applicationsMenu: false,
            dataManagementMenu: false,
            projectMenu: false,
            setupMenu: false,
            userMenu: false,
          });
          this.update({selectedCategory: catName});
        },

        handleCategoryCloseMenu: (menu = ``) => {
          // race condition with toggleSelectedCategory, click twice to go to another menu rather than once
          if (menu) {
            this.update({selectedCategory: ``});
            this.helpers.updateOpen({[menu]: false});
          }
        },

        toggleSelectedCategory: (catName: string) => {
          // open and close menu if header is clicked
          const selectedCategory = this.state.selectedCategory !== catName ? catName : ``;
          this.helpers.selectCategory(selectedCategory);
        },

        handleClickReportName: (reportName: string, source: string) => {
          mixpanel.track(NavigationTrackingEvent.SelectReport, {
            'Nav Source': source,
            'Report From': this.state.currentReportName,
            'Report To': reportName,
          });
        },

        handleErrorToastClosed: () => {
          this.update({errorAlertMsg: null});
        },

        handleNotify: (ev) => {
          const {message} = ev.detail;
          this.addToast(message);
        },

        handleError: (ev) => {
          const {error, message} = ev.detail;
          this.showError(message || error.message, error);
        },

        handleLinkClick: (ev: MouseEvent) => {
          const target = ev.composedPath()[0] as HTMLElement;
          if (target && target.closest) {
            const anchorLink: HTMLAnchorElement = closestReportLink(target);
            // let browser handle open in new tab interaction for a(target=_blank)
            if (anchorLink && anchorLink.target !== `_blank`) {
              const href = anchorLink.getAttribute(`href`);
              this.startNavigationWorkflows(href);
              this.helpers.navigate(href, ev, {fromAnchorlink: true});
            }
          }
        },

        handleSwitchReport: (ev: CustomEvent) => {
          this.helpers.navigate(ev.detail.newUrlPath, ev, {
            keepReportContext: true,
            additionalContext: ev.detail.additionalContext,
          });
        },

        getNavReportsFromCategory: (category: NavCategoryType): NavReports => {
          const reportContext = this.helpers.getReportContext();
          return getNavReportsFromCategory(category, reportContext);
        },

        getReportContext: (): ReportContext => {
          const {projectMenuWorkspaces} = this.state;
          const workspaceId = this.currentWorkspaceId;
          const isProjectOrGlobalWorkspace = this.isProjectOrGlobalWorkspace(workspaceId, projectMenuWorkspaces);
          return {
            projectId: parseInt(getProjectId(), 10),
            project: this.getCurrentProject(),
            isProjectOrGlobalWorkspace,
          };
        },

        chromeHotKeySwitcher: (ev) => {
          return keyEventSwitcher(
            {
              [`${getCommandKey()} + KeyK`]: () => {
                mixpanel.track(NavigationTrackingEvent.OpenPrimaryMenu, {
                  Menu: `search`,
                  ...this.getSearchCommonProperties(),
                });
                this.helpers.handleOpenReportsAndDashboardClicked();
              },
            },
            {stopPropagation: true, usableInInputs: true},
          )(ev);
        },

        getSearchShortcutDisplayName: () => generateShortcutLabel(`K`, {modifiers: [getCommandKey()]}),

        handleSearchModalClose: () => {
          this.helpers.updateOpen({searchModal: false});
          this.update({searchedText: DEFAULT_SEARCH_QUERY_STRING});
        },

        getApplicationsMenuSections: (): Section[] => {
          const {projectId} = this.state;
          const workspaceId = this.currentWorkspaceId;

          const toSectionItem = (app: AppMenuApp) => ({
            label: app.name || ``,
            subLabel: app.description,
            wrapSubLabel: true,
            href: app.url,
            isDisabled: app.isDisabled,
            tooltip: app.tooltipStr
              ? {
                  placement: Placement.Top,
                  text: app.tooltipStr,
                }
              : null,
          });

          const reportContext: ReportContext = this.helpers.getReportContext();
          const applicationReports = [ReportName.JqlConsole, ReportName.Segmentation, ReportName.Revenue];
          const nonDashboardSupportedReports = [ReportName.Signal, ReportName.Impact, ReportName.ExperimentReporting];

          const allApplicationReports = [
            ...nonDashboardSupportedReports,
            ...(!this.helpers.isDemoProject() ? applicationReports : []),
          ].map((report) => {
            const objReport = REPORT_INFO[report];
            const isDisabled = objReport.disabling?.isDisabled(reportContext);
            return {
              name: objReport.label,
              description: objReport.description,
              url: getReportUrl(objReport.endpoint(reportContext), {projectId, workspaceId}),
              hidden: objReport.hidden?.(reportContext),
              isDisabled,
              tooltipStr: isDisabled ? objReport.disabling.disabledMsg : null,
            };
          });

          const result: Section[] = [
            {
              label: `Mixpanel Apps`,
              items: allApplicationReports.filter((app) => !app.hidden).map(toSectionItem),
            },
          ];

          return result;
        },

        getSettingsMenuSections: (): Section[] => {
          const menuItems: SectionItem[] = [
            this.getOrgSettingsMenu(),
            this.getProjectSettingsMenu(),
            {
              label: `Personal Settings`,
              href: `/settings/account`,
              value: `Profile & Preferences`,
              tracking: SETTINGS_CATEGORY_TRACKING.Account,
              id: this.getCurrentUser()?.id,
            },
          ].filter(Boolean);

          // GOOT: logged in users are getting "Leave Demo"
          if (this.getCurrentUserMeta().status === ModelStatus.Loading) {
            return [
              this.getUserProfile(),
              this.getDarkModeSwitcher(),
              {
                items: menuItems,
              },
              {
                items: [{label: `Log Out`}],
              },
            ];
          }

          if (this.isGuestUser) {
            return [
              this.getDarkModeSwitcher(),
              {
                items: menuItems,
              },
              {
                items: [{label: `Leave Demo`}],
              },
            ];
          }

          return [
            this.getUserProfile(),
            this.getDarkModeSwitcher(),
            {
              items: menuItems,
            },
            this.getShortcutMenu(),
            this.determineStartupCreditPromoFeatureEligibility() && {
              items: this.createStartupCreditPromoSectionItem(),
            },
            {
              items: [{label: `Log Out`}],
            },
          ].filter(Boolean);
        },

        handleDarkModeSwitch: (ev) => {
          mixpanel.track(events.userSetDarkMode, {
            enabled: ev.detail.on,
          });
          this.setDarkMode(ev.detail.on, true);
        },

        handleToastCtaAction: (ev) => {
          ev.preventDefault();
          const selectedToastId = ev.target.dataset.id;
          if (selectedToastId && toastCtaCallbackMap.has(selectedToastId)) {
            toastCtaCallbackMap.get(selectedToastId).call(null);
            toastCtaCallbackMap.delete(selectedToastId);
          }
        },

        getUserHasCreateWorkspacePermission: () => {
          const projectId = this.state.projectMenuWorkspaces[0].projectId;
          const project = this.getProject(projectId);
          try {
            return (
              project &&
              this.orgHasPerm(this.getCurrentUser()?.organizations[project.organizationId], Permission.ManageWorkspaces)
            );
          } catch (err) {
            reporter.error(err, {projectId, project, orgIds: this.helpers.getOrgs().map((org) => org.id)});
            return false;
          }
        },

        handleCreateWorkspaceBtnClick: async () => {
          // grab this now because the menu can collapse behind the modal and will 'reset' to the current project based on attrs
          const projectId = this.state.projectMenuWorkspaces[0].projectId;
          try {
            const {didSave, name, description} = await promptManageWorkspaceModal(
              `Create New Data View`,
              {},
              projectId,
            );
            if (didSave) {
              const workspace = await workspacesClient.createWorkspace(projectId, {name, description});
              this.getContext(`projects`).update({
                [projectId]: {
                  workspaces: {[workspace.id]: workspace},
                },
              });
              if (projectId === this.getCurrentProject()?.id) {
                this.getContext(`currentProject`).update({
                  workspaces: {[workspace.id]: workspace},
                });
              }
              if (this.state.projectMenuWorkspaces[0] && this.state.projectMenuWorkspaces[0].projectId === projectId) {
                // the already computed and sorted project menu needs to be updated with the new workspace
                this.update({projectMenuWorkspaces: this.getWorkspacesForProject(projectId)});
              }
              const [eventName, eventProps] = workspaceCreateEvent(
                this.getWorkspacesForProject(projectId).length,
                {
                  name,
                  description,
                  projectId,
                },
                UISourceObject.ChromeHeaderCreateWorkspace,
              );
              mixpanel.track(`${workspacesTrackingPrefix} ${eventName}`, eventProps);
              this.addToast(
                `Workspace ${name} Created`,
                ToastType.SUCCESS,
                `Manage`,
                null,
                `/report/${workspace.projectId}/settings/#project/${workspace.projectId}/views/detail/${workspace.id}`,
              );
            }
          } catch (err) {
            const [eventName, eventProps] = workspaceApiFailure(ApiFailureDescription.Creation, err.message, {
              projectId,
              source: UISourceObject.ChromeHeaderCreateWorkspace,
            });
            mixpanel.track(`${workspacesTrackingPrefix} ${eventName}`, eventProps);
            if (err.message && (err.message as string).includes(`already exists`)) {
              this.addToast(`Data View already exists`, ToastType.ERROR, `Manage Data Views`, () =>
                this.getUrlManager().setLocation(`/settings/project/${projectId}/views`),
              );
            } else {
              this.showError(`Data view creation failed. Please try again or contact support.`, err);
            }
          }
        },

        supportWidgetAttrs: () => {
          const projectDetails = this.getCurrentProject();
          return {
            elref: `supportWidget`,
            'events-plan-id': projectDetails?.eventsPlanID,
            'people-plan-id': projectDetails?.peoplePlanID,
            'billing-account': supportInfoStore.value.billingAccount,
            'service-plan-ids': supportInfoStore.value.servicePlanIds,
            'user-age-in-days': supportInfoStore.value.userAgeDays,
            'user-email': this.getCurrentUser()?.email,
            'user-id': this.getCurrentUser()?.id,
            'user-name': this.getCurrentUser()?.name,
            'project-id': this.state.projectId,
            'should-override-free-warning': this.helpers.isInFreeSupportExperiment(),
          };
        },

        isInFreeSupportExperiment: () => {
          return this.getCurrentProject()?.hasFeatureFlag(ProjectFeatureFlag.FreeSupportTrial);
        },

        shouldShowHelpMenuNotif: (): boolean => {
          return this.state.isNewUser || !!this.getUnseenProductUpdates().length;
        },

        shouldShowSettingsMenuNotif: (): boolean => {
          const currentProject = this.getCurrentProject();
          const featureEligible = this.determineStartupCreditPromoFeatureEligibility();
          if (typeof mixpanel !== `undefined` && getCookie(`mp_account_id`) && currentProject) {
            mixpanel
              .get_group(`Project owner billing account ID`, getCookie(`mp_account_id`))
              .set({'[Account] Startup Credit Promo Eligible': featureEligible});
          }

          return currentProject
            ? currentProject.hasStartupCreditAndFreeUser() &&
                !this.state.viewedStartupCreditPromo &&
                this.state.viewedReportCount > 1 &&
                featureEligible
            : false;
        },
        getStartupCreditBalances: (): CreditBalanceInfo | undefined => {
          return this.getCurrentProject()?.getStartupCreditBalances();
        },

        getStartupCreditInfo: (): CreditInfo | undefined => {
          return this.getCurrentProject()?.getStartupCreditInfos()[0];
        },

        dismissOnboardingChecklist: (): void => {
          setUserFlag(UserFlag.DismissedOnboardingChecklist);
          this.update({dismissedOnboardingChecklist: true, showOnboardingChecklistHelpTooltip: true});

          window.setTimeout(() => {
            this.update({showOnboardingChecklistHelpTooltip: false});
          }, 2500);
        },
        completeOnboardingChecklist: (): void => {
          this.update({dismissedOnboardingChecklist: true});
        },
      },
    };
  }

  getReportUrl(endpoint: string) {
    return getReportUrl(endpoint, {projectId: this.state.projectId, workspaceId: this.currentWorkspaceId});
  }

  getBrowseUrl(options: GetBrowseUrlOptions) {
    return getBrowseUrl(this.getUrlManager(), {
      ...options,
      projectId: this.state.projectId,
      workspaceId: this.currentWorkspaceId,
    });
  }

  private getGettingStartedSubMenu() {
    if (this.state.dismissedOnboardingChecklist) {
      return {
        items: [
          {
            label: HelpLabels.OnboardingGuide,
            icon: `type-list`,
          },
          ...HELP_GETTING_STARTED_SECTIONS.items,
        ],
      };
    }

    return HELP_GETTING_STARTED_SECTIONS;
  }

  private determineStartupCreditPromoFeatureEligibility(): boolean {
    const currentProject = this.getCurrentProject();
    if (!currentProject) {
      return false;
    }
    if (this.startupCreditPromoFeatureEligible !== null) {
      return this.startupCreditPromoFeatureEligible;
    }
    const featureEligible = currentProject
      ? featureSamplingUtils.determineFeatureEligibility(
          currentProject.organizationId?.toString(),
          `startup-credit-promo`,
          100,
        )
      : false;
    this.startupCreditPromoFeatureEligible = featureEligible;
    setPeopleProperties({'Startup Credit Promo Eligible': featureEligible});
    return featureEligible;
  }

  private getOrgSettingsMenu(): SectionItem | null {
    const currentProject = this.getCurrentProject();
    const currentOrganization = this.getCurrentUser()?.organizations[currentProject.organizationId];
    const canAccessOrgSettings = currentOrganization?.permissions?.includes(Permission.ViewOrganizationSettings);

    if (currentOrganization) {
      return {
        ...currentOrganization,
        wrapLabel: true,
        label: `Organization Settings`,
        href: getReportUrl(`settings/#org/${currentOrganization.id}`, {projectId: currentProject.id}),
        isDisabled: !canAccessOrgSettings,
        tooltip: {
          text: canAccessOrgSettings
            ? `${currentOrganization.name} Settings`
            : `Only users with an owner or admin role can access ${currentOrganization.name} Organization Settings`,
          size: DisplaySize.Medium,
          placement: Placement.Left,
        },
        tracking: SETTINGS_CATEGORY_TRACKING.Org,
      };
    }

    if (this.getCurrentUser()?.isStaff) {
      // staff should always receive org settings
      const orgId = currentProject.organizationId;
      return {
        wrapLabel: true,
        label: `Organization Settings`,
        href: getReportUrl(`settings/#org/${orgId}`, {projectId: currentProject.id}),
        tooltip: {
          text: `Organization Settings`,
          size: DisplaySize.Medium,
          placement: Placement.Left,
        },
        tracking: SETTINGS_CATEGORY_TRACKING.Org,
      };
    }

    return null;
  }

  private getProjectSettingsMenu(): SectionItem {
    const canAccessProjectSettings = this.getCurrentProject()?.hasPermission(Permission.AccessProjectSettings);
    return {
      label: `Project Settings`,
      wrapLabel: true,
      href: getReportUrl(`settings/#project/${this.getCurrentProjectId()}`, {
        projectId: this.getCurrentProjectId(),
      }),
      isDisabled: !canAccessProjectSettings,
      tooltip: {
        text: canAccessProjectSettings
          ? `${this.getCurrentProjectName()} Settings`
          : `Only users with an owner or admin role can access ${this.getCurrentProjectName()} Project Settings`,
        size: DisplaySize.Medium,
        placement: Placement.Left,
      },
      value: this.getCurrentProjectName(),
      tracking: SETTINGS_CATEGORY_TRACKING.Project,
    };
  }

  private getShortcutMenu() {
    const currentProject = this.getCurrentProject();
    const currentOrganization = this.getCurrentUser()?.organizations[currentProject.organizationId];

    const shortcutItems: SectionItem[] = [
      {
        label: `Set Up Mixpanel`,
        href: getReportUrl(`setup/`, {projectId: this.getCurrentProjectId()}),
        value: `Setup Mixpanel`,
        tracking: SETTINGS_CATEGORY_TRACKING.GettingStarted,
      },
    ];

    if (currentOrganization?.permissions?.includes(Permission.ViewOrganizationSettings)) {
      shortcutItems.push({
        label: `Plan Details and Billing`,
        value: `Plan Details and Billing`,
        href: getReportUrl(`settings/#org/${currentOrganization.id}/plan`, {projectId: this.getCurrentProjectId()}),
        tracking: SETTINGS_CATEGORY_TRACKING.BillingDetails,
      });
    }

    if (currentOrganization?.permissions.includes(Permission.AddUser)) {
      shortcutItems.push({label: `Invite Users`});
    }

    // boolean used for getting users back to FYT
    // for FYT users, will only have demo project org available
    const userIsFromFindYourTeam =
      this.getCurrentUserMeta().status === ModelStatus.Success &&
      this.helpers.getOrgs({excludeDemoOrgs: true}).length === 0;

    if (userIsFromFindYourTeam) {
      shortcutItems.push({
        label: `Back to Sign Up`,
        icon: `arrow-left`,
        href: `/find-your-org`,
        tracking: events.clickBackToSignup,
      });
    }

    return {
      label: `Shortcuts`,
      items: shortcutItems,
    };
  }

  private getDarkModeSwitcher(): Section {
    return {
      actionsRenderer: () =>
        SwitchEl({
          attrs: {
            on: this.state.darkMode,
          },
          on: {
            change: this.helpers.handleDarkModeSwitch,
          },
        }),
      items: [
        {
          label: `Dark Mode`,
        },
      ],
    };
  }

  private getUserProfile(): Section {
    const roleName = capitalize(this.getCurrentProject()?.role?.name || `----`);
    const profileName = this.getCurrentUser()?.name || `User`;

    return {
      iconRenderer: ({id, name}: SectionItem) =>
        UserIconEl({attrs: {id, name, 'disable-tooltip': true, size: IconSizeType.Medium}}),
      items: [
        {
          label: `${profileName}`,
          subLabel: roleName,
          labelTheme: LabelTheme.Strong,
          subLabelTheme: SubLabelTheme.Soft,
          id: this.getCurrentUser()?.id,
          name: profileName,
          isReadOnly: true,
          value: `Profile & Preferences`,
          tracking: SETTINGS_CATEGORY_TRACKING.Account,
        },
      ],
    };
  }

  private createStartupCreditPromoSectionItem(): SectionItem[] {
    const currentProject = this.getCurrentProject();
    const startupCreditPromo = [];
    const startupCreditBalances = currentProject?.getStartupCreditBalances();
    if (currentProject?.hasStartupCreditAndFreeUser()) {
      startupCreditPromo.push({
        label: `Upgrade to Growth Plan`,
        subLabel: TextHighlightEl({
          attrs: {
            theme: THEME.INFORMATION,
            text: `${numberInUSD(startupCreditBalances.creditUsageAmount, {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            })} of ${numberInUSD(startupCreditBalances.initialValue / 1000, {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            })}k credits used`,
            length: `wide`,
          },
        }),
        hasCaret: true,
        persistCaret: true,
        caretStyle: `arrow`,
        href: `/pricing/plan-builder`,
        highlightItem: currentProject?.hasStartupCreditAndFreeUser(),
      });
    }

    return startupCreditPromo;
  }

  resourceCustomSubscriptions = new Map<typeof ModelResource, () => void>([
    [
      CurrentUserResource,
      () => {
        this.tryRedirectToWorkspace();
      },
    ],
    [
      ProjectsResource,
      () => {
        this.tryRedirectToWorkspace();
      },
    ],
    [
      CurrentProjectResource,
      () => {
        const currentProject = this.getCurrentProject();
        // if current project has workspaces but we don't have a workspaceId
        // set a workspaceId from mp_persistence cookie or the project's global workspace
        // and redirect the user to the url with this valid workspaceId
        const stateUpdate = {};
        // check if the current workspaceId is still valid. It can be invalid, if a user leaves a workspace
        // in data views library.
        if (currentProject) {
          this.update({categories: this.getCategories()});
          this.helpers.updateCurrentCategoryAndReportNameFromUrl();
          // If a staff user doesn't have access to any workspaces, we keep the workspace_id that was initialized
          // this prevents workspace_id from being changed as a staff user moves from app to app.
          if (this.getCurrentUser()?.isStaff && !Object.keys(currentProject.workspaces).length) {
            this.update(stateUpdate);
            return;
          }

          if (currentProject.hasFeatureFlag(ProjectFeatureFlag.FestiveModeStage)) {
            setFestiveMode(FestiveMode.Luv);
          } else if (this.getCurrentUser()?.hasFeatureFlag(UserFeatureFlag.FestiveModeStage)) {
            // mostly to delight a twitter user https://twitter.com/JonCarden/status/1287755477082923009
            // see https://mixpanel.slack.com/archives/C0W91MX2Q/p1595864326150400
            setFestiveMode(FestiveMode.HorseRun);
          }

          const currentWorkspaceId = this.currentWorkspaceId;
          const validWorkspaceId = this.findCurrentValidWorkspaceId(currentWorkspaceId);
          if (validWorkspaceId !== currentWorkspaceId && this.state.currentReportName) {
            const fragment = this.getUrlManager().getLocation().hash;
            const path = makePathFromUrlState({
              projectId: this.state.projectId.toString(),
              workspaceId: validWorkspaceId.toString(),
            });

            const location = path + fragment;

            mixpanel.track(
              `Invalid workspace redirect`,
              {
                'Old workspace': currentWorkspaceId,
                'New workspace': validWorkspaceId,
              },
              () => {
                this.getUrlManager().setLocation(location);
              },
            );
          }

          // projectMenuWorkspaces
          const projectMenuWorkspaces = this.getWorkspacesForProject(currentProject.id);
          if (projectMenuWorkspaces.length) {
            Object.assign(stateUpdate, {projectMenuWorkspaces});
          }
        }

        if (!currentProject.isDemo) {
          this.lastUserProjectStorage.setItem({
            key: LAST_USER_PROJECT_KEY,
            userId: userCookieUtils.getUserIdentityFromCookie()?.id,
            value: {
              projectId: currentProject.id,
            },
          });
        }

        // the function should always run update()
        this.update(stateUpdate);

        this.helpers.initializeSpaNavManager();
      },
    ],
  ]);

  private tryRedirectToWorkspace() {
    const currentUser = this.getCurrentUser();
    const allProjects = this.getAllProjects();
    if (!currentUser || !allProjects) {
      return;
    }

    const isStaff = currentUser.isStaff;
    if (isStaff) {
      return;
    }
    const currentProjectId = parseInt(getProjectId(), 10);
    const currentWorkspaceId = this.currentWorkspaceId;
    const currentProject = allProjects.find((project) => project.id === currentProjectId);
    const currentWorkspace = allProjects
      .map((project) => Object.values(project.workspaces))
      .flat()
      .find((workspace) => workspace.id === currentWorkspaceId);
    // catches the case when the workspace is valid and the current user has access, but the project is invalid (for example, a project that's been reset)
    if (!currentProject && currentWorkspace) {
      const fragment = this.getUrlManager().getLocation().hash;
      const path = makePathFromUrlState({
        projectId: currentWorkspace.projectId.toString(),
        workspaceId: currentWorkspace.id.toString(),
      });

      const location = path + fragment;

      console.error(`Invalid project, redirecting to valid workspace`);
      mixpanel.track(
        `Invalid project redirect`,
        {
          'Old project': currentProjectId,
          'New project': currentWorkspace.projectId,
        },
        () => {
          this.getUrlManager().setLocation(location);
        },
      );
    }
  }

  setDarkMode(on: boolean, shouldPersist: boolean) {
    if (on) {
      document.documentElement.classList.add(`dark-mode`);
    } else {
      document.documentElement.classList.remove(`dark-mode`);
    }

    if (shouldPersist) {
      this.userLocalStorageDarkModeHelper.setItem({
        key: DARK_MODE_KEY,
        userId: userCookieUtils.getUserIdentityFromCookie()?.id,
        value: {darkMode: on},
      });
    }
    this.update({darkMode: on});
  }

  findCurrentValidWorkspaceId(firstPriorityWorkspaceId: number | null): number | null {
    const currentProject = this.getCurrentProject();
    if (!currentProject) {
      return null;
    }
    return this.getValidWorkspaceId(currentProject, [
      firstPriorityWorkspaceId,
      this.getWorkspaceIdFromPersistenceCookie(currentProject.id),
      this.getGlobalWorkspaceIdForProject(currentProject.id),
    ]);
  }

  getValidWorkspaceId(project: Project, workspaceIdPriorityList: number[]) {
    const userWorkspaces = this.getWorkspacesForProject(project ? project.id : this.state.projectId) || [];
    for (const workspaceId of workspaceIdPriorityList) {
      const found = userWorkspaces.find((workspace) => workspace.id === workspaceId);
      if (found) {
        return found.id;
      }
    }
    return userWorkspaces.length ? userWorkspaces[0].id : null;
  }

  handleSupportInfo() {
    activatePersistedZendeskChat(ChatDepartment.Support, this.getCurrentUser()?.name, this.getCurrentUser()?.email);
    this.update();
  }

  public connectedCallback() {
    super.connectedCallback();
    this.initialize = this.initialize.bind(this);
    let isInitialized = false;
    // projectId and workspaceId is present on url 99% of the time but for some url e.g settings, it's not.
    // we have to wait for DOMContentLoaded for those cases
    if (getProjectId()) {
      this.initialize();
      isInitialized = true;
    }
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has(`contact-us`) && searchParams.get(`contact-us`) !== `false`) {
      this.update({
        isSupportWidgetMaximized: true,
        showSupportWidget: true,
      });
    }

    if (searchParams.has(`submit-feedback`) && searchParams.get(`submit-feedback`) !== `false`) {
      promptFeedbackModal({
        feature: FeedbackForFeature.General,
      });
    }

    if (this.isDownloadMode()) {
      layoutManager.setConfig({
        hideGlobalActions: true,
        bannerContainer: document.createElement(`div`),
      });
    }

    this.userLocalStorageDarkModeHelper = new UserLocalStorageHelper({
      context: this.getContext(`localStorageManager`),
      namespace: LocalStorageKey.DarkMode,
    });
    const persistedState = this.userLocalStorageDarkModeHelper.getItem({
      key: DARK_MODE_KEY,
      userId: userCookieUtils.getUserIdentityFromCookie()?.id,
    });

    if (persistedState) {
      this.setDarkMode(persistedState.darkMode, false);
    } else {
      const isOSDefault = window.matchMedia(`(prefers-color-scheme: dark)`).matches;
      this.setDarkMode(isOSDefault, false);
    }

    this.lastUserProjectStorage = new UserLocalStorageHelper({
      context: this.getContext(`localStorageManager`),
      namespace: LocalStorageKey.LastUserProject,
    });

    // mp.report.globals are initialized after chrome-header is connected
    // so we need to wait for DOMContentLoaded
    window.addEventListener(
      `DOMContentLoaded`,
      () => {
        if (!isInitialized) {
          this.initialize();
        }
      },
      {once: true},
    );

    document.addEventListener(`openContactSupport`, this.openContactSupport);
  }

  public initialize() {
    const projectId = parseInt(getProjectId(), 10);
    const location = this.getUrlManager().getLocation();
    this.update({projectId, currentUrlPath: location.pathname});

    // clear out volt shell elements if any. mp-chrome-header's #shadow-root is unaffected
    this.innerHTML = ``;

    this.handleSupportInfo = this.handleSupportInfo.bind(this);

    supportInfoStore.subscribe(this.handleSupportInfo).fetch();

    supportInfoStore.fetch();
    userDetailsStore.fetch();

    // projectId could be empty if user does not have access to any project
    // see https://mixpanel.slack.com/archives/C014L0QMH1T/p1592860852025000
    if (projectId) {
      // TODO remove this once we're sure no other apps/components are relying on
      // chrome-header initiating this fetch for them...
      currentProjectStore.fetch();
    } else {
      reporter.info(`Cannot fetch current project id`, {url: location.href});
    }

    const projectMenuWorkspaces = this.getWorkspacesForProject(projectId);
    this.update({projectMenuWorkspaces});

    this.helpers.initializeSpaNavManager();

    window.addEventListener(`popstate`, this.helpers.handleHistoryStateChange);
    window.addEventListener(`pushstate`, this.helpers.handleHistoryStateChange);
    window.addEventListener(`replacestate`, this.helpers.handleHistoryStateChange);
    document.addEventListener(`click`, this.helpers.handleLinkClick);
    document.addEventListener(`reportswitch`, this.helpers.handleSwitchReport);
    this.el.addEventListener(`click`, this.helpers.handleLinkClick); // catch link clicks within shadow dom

    this.setStateByUserFlagsValueAndPromptModals();
    this.getProductUpdates();
    this.showTourFromUrl();
  }

  public disconnectedCallback() {
    window.removeEventListener(`popstate`, this.helpers.handleHistoryStateChange);
    window.removeEventListener(`pushstate`, this.helpers.handleHistoryStateChange);
    window.removeEventListener(`replacestate`, this.helpers.handleHistoryStateChange);
    document.removeEventListener(`click`, this.helpers.handleLinkClick);
    document.removeEventListener(`reportswitch`, this.helpers.handleSwitchReport);
    this.el.removeEventListener(`click`, this.helpers.handleLinkClick);
    document.removeEventListener(`keydown`, this.helpers.chromeHotKeySwitcher);
    document.removeEventListener(`openContactSupport`, this.openContactSupport);

    super.disconnectedCallback();
  }

  getCurrentProject(): CurrentProject | undefined {
    return this.getCurrentProjectMeta().value;
  }

  getCurrentProjectId() {
    return Number(this.getContext(`currentProject`).id);
  }

  getCurrentProjectMeta(): Model<CurrentProject> {
    return this.getContext(`currentProject`).get();
  }

  getCurrentProjectName() {
    return this.getCurrentProjectMeta().status === ModelStatus.Loading ? `Loading ...` : this.getCurrentProject().name;
  }

  getCurrentUser(): CurrentUser {
    return this.getCurrentUserMeta().value;
  }

  getCurrentUserMeta(): Model<CurrentUser> {
    return this.getContext(`currentUser`).get();
  }

  getProject(projectId): Project {
    return this.getContext(`projects`).getOne(projectId).value;
  }

  get isGuestUser() {
    return this.getCurrentUserMeta().status === ModelStatus.Success && this.getCurrentUser().isGuest;
  }

  get currentWorkspaceId(): number | null {
    // rely on the url for the current workspace id
    let currentWorkspaceId = parseInt(getWorkspaceId(this.getUrlManager()), 10) || null; // null instead of NaN

    // project only apps never have the workspace id in the url; for these apps we add this special logic to
    // obtain a valid workspace id from outside the url
    if (this.getCurrentProject() && isProjectOnlyApp(this.state.currentReportName)) {
      currentWorkspaceId = this.findCurrentValidWorkspaceId(currentWorkspaceId);
    }

    return currentWorkspaceId;
  }

  public isDownloadMode(): boolean {
    return getDownloadMode(this.getUrlManager()) !== DownloadMode.None;
  }

  private getCategories(): NavCategories {
    return getNavCategories({reportContext: this.helpers.getReportContext()});
  }

  private getWorkspacesForProject(projectId: number): WorkspaceObj[] {
    const project = projectId === this.getCurrentProjectId() ? this.getCurrentProject() : this.getProject(projectId);
    return Object.values(project?.workspaces ?? {}).sort(sortWorkspaces);
  }

  private isProjectOrGlobalWorkspace(workspaceId: number, projectMenuWorkspaces: WorkspaceObj[]) {
    if (projectMenuWorkspaces) {
      const workspace = projectMenuWorkspaces.find((ws) => ws.id === workspaceId);
      return !workspace || workspace.isGlobal;
    }
    return true;
  }

  private orgHasPerm(org: OrgInfo, perm: string) {
    if (!org || !perm) {
      return false;
    } else {
      return Array.isArray(org.permissions) && org.permissions.includes(perm);
    }
  }

  private trackSettingsMenuItemSelected(item) {
    const {org, tracking} = item;

    if (tracking) {
      if (tracking === events.viewOrg && org) {
        track(tracking, {
          [properties.orgId]: org.id,
          [properties.orgName]: org.name,
        });
      } else {
        track(tracking);
      }
    }
  }

  getUrlManager() {
    return this.getContext(`urlManager`);
  }

  private addToast(message: string, toastType?: ToastType, ctaLabel?, ctaCallback?, ctaHref?) {
    const newToast = {message, ttl: TOAST_TTL, type: toastType, cta: ctaLabel, ctaCallback, ctaHref};
    addToast(newToast);
  }

  private showError(errorAlertMsg: string, err: Error) {
    if (err) {
      console.error(err);
    }
    this.update({errorAlertMsg});
  }

  private getReportInfoFromUrl(url: URL): {categoryName: string; reportName: string; reportEndpoint: string} | null {
    // go through every report and see if we have an endpoint match
    for (const [categoryName, category] of Object.entries(this.state.categories || {})) {
      for (const [reportName, report] of Object.entries(category.reports)) {
        const [endpointPath, endpointHash] = report.endpoint.split(`#`);

        // allow trailing hash in endpoint match
        const hasEndpointMatch =
          url.pathname.endsWith(`/${endpointPath}`) ||
          url.pathname.endsWith(`/${endpointPath}/`) ||
          report.endpointAliases?.some(
            (alias: string) => url.pathname.endsWith(`/${alias}`) || url.pathname.endsWith(`/${alias}/`),
          );
        const hasHashMatch = !endpointHash || url.hash.startsWith(`#${endpointHash}`);
        if (hasEndpointMatch && hasHashMatch) {
          return {
            categoryName,
            reportName,
            reportEndpoint: report.endpoint,
          };
        }
      }
    }

    // if endpoint isn't found in categories tree
    const reportEndpoint = parseStateFromUrl(url.pathname)?.appName;
    if (reportEndpoint) {
      return {
        categoryName: ``,
        reportName: reportEndpoint,
        reportEndpoint,
      };
    }

    return null;
  }

  private async persistLastSeenUrlData(): Promise<void> {
    const location = this.getUrlManager().getLocation();
    const url = location.href;
    const reportInfo = this.getReportInfoFromUrl(new URL(url));
    if (!isPersistableReport()) {
      debug.warn(`Current report not persistable`);
    } else if (!reportInfo) {
      reporter.warn(`Report info not found for given URL`, {url});
    } else {
      // currentProject must be set before we can get the actual valid workspace id
      if (this.getCurrentProject()) {
        // we need to check the the current workspace id is equal to the valid workspace id
        // or we run the risk of setting the wrong workspace id in the cookie
        let workspaceId = this.currentWorkspaceId;
        const validWorkspaceId = this.findCurrentValidWorkspaceId(workspaceId);
        if (validWorkspaceId !== workspaceId) {
          workspaceId = validWorkspaceId;
        }
        await safeSetCookie(
          REPORT_PERSISTENCE_COOKIE,
          JSON.stringify({
            project: this.state.projectId,
            ...(workspaceId ? {workspace: workspaceId} : {}),
            report_path: reportInfo.reportEndpoint,
          } as ReportPersistenceCookie),
          14 /*days*/,
        );
      }
    }
  }

  private getHelpLinkIcon(href) {
    const {hostname} = new URL(href);
    return /.?slack.com/.test(hostname) ? `slack-logo` : `share`;
  }

  private getWorkspaceIdFromPersistenceCookie(projectId: number): number | null {
    try {
      const persistenceCookie: ReportPersistenceCookie = JSON.parse(getCookie(REPORT_PERSISTENCE_COOKIE) || `{}`);
      if (persistenceCookie.workspace && persistenceCookie.project === projectId) {
        return persistenceCookie.workspace;
      }
    } catch (_err) {
      /* fail gracefully*/
    }
    return null;
  }

  private getGlobalWorkspaceIdForProject(projectId: number): number | null {
    const projectWorkspaces = this.getWorkspacesForProject(projectId);
    const globalWorkspace = projectWorkspaces.find((workspace) => workspace.isGlobal);
    return globalWorkspace ? globalWorkspace.id : null;
  }

  private hideAllWidgets() {
    this.update({showSupportWidget: false});
    hideZendeskWidget();
  }

  private getSearchCommonProperties() {
    return {
      ...getSearchCommonTrackingProperties(),
      [SearchTrackedProperty.Source]: SearchSource.Dropdown,
      [SearchTrackedProperty.ContentDiscoveryType]: ContentDiscoveryType.Browse,
    };
  }

  private getSearchPhaseVersion() {
    return SearchPhaseVersion.AddedUniqueViewsCustomRank;
  }

  private async showTourFromUrl() {
    const urlManager = this.getUrlManager();
    const tourId = urlManager.getQueryStringParam(CHAMELEON_TOUR_QUERY_PARAM) as TourId;
    if (tourId) {
      await chameleonReadyPromise();
      showTour(tourId);
    }
  }

  private async setStateByUserFlagsValueAndPromptModals() {
    const flags = await getUserFlags();
    const isNewUser = !(UserFlag.ViewedReportCount in flags);
    const viewedReportCount = flags.VIEWED_REPORT_COUNT || 0;
    const viewedStartupCreditPromo = UserFlag.ViewedStartupCreditPromo in flags;
    const dismissedOnboardingChecklist = UserFlag.DismissedOnboardingChecklist in flags;
    const enableOnboardingChecklist = UserFlag.EnableOnboardingChecklist in flags;

    this.update({
      isNewUser,
      viewedReportCount,
      viewedStartupCreditPromo,
      dismissedOnboardingChecklist,
      enableOnboardingChecklist,
    });

    viewedReportFlagManager.increment();

    const showFYTModal =
      !(UserFlag.ViewedFYTModal in flags) &&
      userDetailsStore.value?.organizations?.[this.getCurrentProject()?.organizationId]?.should_show_fyt_modal;

    if (showFYTModal) {
      this.setViewedFYTModal();
      await promptFYTModal();
    }
  }

  public setViewedStartupCreditPromo = () => {
    setUserFlag(UserFlag.ViewedStartupCreditPromo);
    userFlagsStore.update({[UserFlag.ViewedStartupCreditPromo]: 1});
    this.update({viewedStartupCreditPromo: true});
  };

  public setViewedFYTModal = () => {
    setUserFlag(UserFlag.ViewedFYTModal);
    userFlagsStore.update({[UserFlag.ViewedFYTModal]: 1});
  };

  private getFirstName() {
    return (this.getCurrentUser()?.name || ``).split(` `)[0];
  }

  private getAllProjects() {
    return this.getContext(`projects`).getAllValues();
  }

  private async getProductUpdates() {
    const productUpdates = await getProductUpdates();
    this.update({productUpdates});
  }

  private getUnseenProductUpdates() {
    return this.state.productUpdates.filter((update) => !update.seen);
  }

  private markProductUpdatesAsSeen() {
    const {productUpdates} = this.state;
    const unseen = this.getUnseenProductUpdates();

    if (unseen.length) {
      markProductUpdatesAsSeen(unseen.map(({id}) => id));
      this.update({
        productUpdates: productUpdates.map((update) => ({...update, seen: true})),
      });
    }
  }

  private startNavigationWorkflows(urlToNavigate: string) {
    if (this.state.currentReportName === ReportName.Dashboards && isSavedCoreReportUrl(urlToNavigate)) {
      captureWorkflowTiming(this, {
        name: `Dashboards => Core Report Query`,
        key: `navigation-to-saved-report-query-${urlToNavigate}`,
        isComplete: (ev) => ev.detail.eventName.includes(QueryStartEvent),
        additionalProps: {tab: parseStateFromUrl(urlToNavigate).appName},
      });
    }
  }

  openContactSupport = () => {
    mixpanel.track(HelpTrackingEvent.Support);
    this.hideAllWidgets();
    this.update({showSupportWidget: true});
  };
}

export const ChromeHeaderEl = defineAndMakeJsxFactory(`mp-chrome-header`, ChromeHeader);
export const ChromeHeaderReact = panel2react(`mp-chrome-header`, ChromeHeader);
